import React from 'react'
import useValidation, {
  isValidDate,
  isValidEmail,
  isValidPhone,
} from '../../../hooks/useValidation'
import {
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material'
import DesktopDatePicker from '@mui/lab/DesktopDatePicker'
import MobileDatePicker from '@mui/lab/MobileDatePicker'

import CustomInpuText from '../../customInputText/CustomInputText'
import PrimaryButton from '../../primaryButton/PrimaryButton'
import Indicators from '../../indicators/Indicators'
import useWidth from '../../../hooks/useWidth'
import { mdDown, mdUp, smDown } from '../../../utils/breakpoints'

import { useStyles } from './styles'

const Step1 = ({
  btnAction,
  handleGoto,
  handleChange,
  data,
  cantItems,
  current,
}) => {
  const classes = useStyles()
  const width = useWidth()
  let step = data.step

  const validationSchema = {
    name: {
      valid: data.name || false,
      errorMsg: 'Please fill this field.',
      validate: (value) => value || false,
    },
    email: {
      valid: data.email || false,
      errorMsg: 'Email format invalid.',
      validate: (value) => (value ? isValidEmail(value) : false),
    },
    phone: {
      valid: data.phone || false,
      errorMsg: 'Phone format invalid.',
      validate: (value) => (value ? isValidPhone(value) : false),
    },
    associationName: {
      valid: data.associationName || false,
      errorMsg: 'Please fill this field.',
      validate: (value) => value || false,
    },
    role: {
      valid: data.role || false,
      errorMsg: 'Please fill this field.',
      validate: (value) => value || false,
    },
    otherRole: {
      valid: data.role === 'Other' ? data.otherRole || false : true,
      errorMsg: 'Please fill this field.',
      validate: (value) => (data.role === 'Other' ? value || false : true),
    },
    dateSubmission: {
      valid:
        data.role === 'Realtor'
          ? isValidDate(data.dateSubmission) || false
          : true,
      errorMsg: 'Please fill this field.',
      validate: (value) =>
        data.role === 'Realtor' ? isValidDate(value) || false : true,
    },
  }

  const { errors, validate, validateAll } = useValidation(validationSchema)

  const handleInternalChange = (evt) => {
    const { name, value } = evt.target
    handleChange(evt)
    validate(name, value)
  }

  const handleInternalDateChange = (value) => {
    const name = 'dateSubmission'
    handleChange({
      target: {
        name,
        value,
      },
    })
    validate(name, value)
  }

  const handleInternalBtnAction = (evt) => {
    if (validateAll() && step === 1) {
      step++
      btnAction(evt)
    }
  }

  const handleInternalGoto = (index) => {
    if (validateAll()) {
      handleGoto(index)
    }
  }

  return (
    <Grid
      container
      spacing={smDown(width) ? 8 : mdDown(width) ? 4 : 2}
      className={classes.wrapper}
    >
      <Grid item xs={12}>
        <CustomInpuText
          label='Name'
          name='name'
          value={data.name || ''}
          onChange={handleInternalChange}
          required
          error={errors?.name ? true : false}
          helperText={errors?.name || ''}
        />
      </Grid>
      <Grid item xs={12}>
        <CustomInpuText
          label='Email'
          name='email'
          value={data.email || ''}
          onChange={handleInternalChange}
          required
          error={errors?.email ? true : false}
          helperText={errors?.email || ''}
        />
      </Grid>
      <Grid item xs={12}>
        <CustomInpuText
          label='Phone'
          name='phone'
          value={data.phone || ''}
          onChange={handleInternalChange}
          required
          error={errors?.phone ? true : false}
          helperText={errors?.phone || ''}
        />
      </Grid>
      <Grid item xs={12}>
        <CustomInpuText
          label='Community Association Name'
          name='associationName'
          value={data.associationName || ''}
          onChange={handleInternalChange}
          required
          error={errors?.associationName ? true : false}
          helperText={errors?.associationName || ''}
        />
      </Grid>
      <Grid item xs={12}>
        <FormControl fullWidth error={errors?.role ? true : false}>
          <InputLabel id='select-role-label'>Role</InputLabel>
          <Select
            labelId='select-role-label'
            id='select-role'
            value={data.role || ''}
            label='Role'
            name='role'
            required
            onChange={handleInternalChange}
          >
            <MenuItem value={'Resident'}>Resident</MenuItem>
            <MenuItem value={'Board Member'}>Board Member</MenuItem>
            <MenuItem value={'Vendor'}>Vendor</MenuItem>
            <MenuItem value={'Realtor'}>Realtor</MenuItem>
            <MenuItem value={'Other'}>Other</MenuItem>
          </Select>
          {errors?.role && <FormHelperText>{errors?.role}</FormHelperText>}
        </FormControl>
      </Grid>
      {data.role === 'Realtor' && (
        <Grid item xs={12}>
          {mdUp(width) ? (
            <DesktopDatePicker
              label='Date Submission'
              inputFormat='MM/DD/yyyy'
              value={data.dateSubmission}
              onChange={handleInternalDateChange}
              renderInput={(params) => (
                <CustomInpuText
                  required
                  {...params}
                  error={errors?.dateSubmission ? true : false}
                  helperText={errors?.dateSubmission || ''}
                />
              )}
            />
          ) : (
            <MobileDatePicker
              label='Date Submission'
              inputFormat='MM/DD/yyyy'
              value={data.dateSubmission}
              onChange={handleInternalDateChange}
              renderInput={(params) => (
                <CustomInpuText
                  required
                  {...params}
                  error={errors?.dateSubmission ? true : false}
                  helperText={errors?.dateSubmission || ''}
                />
              )}
            />
          )}
        </Grid>
      )}
      {data.role === 'Other' && (
        <Grid item xs={12}>
          <CustomInpuText
            label='Other Role'
            name='otherRole'
            value={data.otherRole || ''}
            onChange={handleInternalChange}
            required
            error={errors?.otherRole ? true : false}
            helperText={errors?.otherRole || ''}
          />
        </Grid>
      )}
      <Grid
        container
        item
        xs={12}
        sm={6}
        lg={6}
        justifyContent='center'
        alignItems='flex-end'
        sx={{ display: { sm: 'none', xs: 'flex' } }}
      >
        <Indicators
          cantItems={cantItems}
          current={current}
          handleGoto={handleInternalGoto}
        />
      </Grid>
      <Grid item xs={12} sm={6} lg={6} className={classes.btnWrapper}>
        <PrimaryButton onClick={handleInternalBtnAction}>Next</PrimaryButton>
      </Grid>
      <Grid
        container
        item
        xs={12}
        sm={6}
        lg={6}
        justifyContent='flex-end'
        alignItems='flex-end'
        sx={{ display: { sm: 'flex', xs: 'none' } }}
      >
        <Indicators
          cantItems={cantItems}
          current={current}
          handleGoto={handleInternalGoto}
        />
      </Grid>
    </Grid>
  )
}

export default Step1
